import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

// Shared city/state autocomplete backed by the free Photon (Komoot) geocoder.
// Used by onboarding and the Creative Maker seller tab.
const PHOTON_URL = "https://photon.komoot.io/api/";
const PLACE_OSM_VALUES = new Set([
  "city", "town", "village", "hamlet", "borough", "suburb", "neighbourhood",
  "quarter", "locality", "county", "state", "region", "municipality",
]);

const formatPhotonSuggestion = (p) => {
  const parts = [p.name];
  if (p.city && p.city !== p.name) parts.push(p.city);
  if (p.state && p.state !== p.name && p.state !== p.city) parts.push(p.state);
  if (p.country && p.country !== p.state) parts.push(p.country);
  return parts.filter(Boolean).join(", ");
};

export const CityAutocomplete = ({
  value,
  onChange,
  label = "Primary market (city & state)",
  placeholder = "Start typing, e.g. Los Angeles, CA",
  inputTestId = "ob-city",
  suggestionsTestId = "ob-city-suggestions",
  focusColor = "#1D6AFF",
  onEnter,
}) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const skipNextFetchRef = useRef(false);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (value !== query) { skipNextFetchRef.current = true; setQuery(value || ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (skipNextFetchRef.current) { skipNextFetchRef.current = false; return; }
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const res = await fetch(`${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=8&lang=en`, { signal: ctrl.signal });
        const json = await res.json();
        const feats = Array.isArray(json?.features) ? json.features : [];
        const places = feats.map((f) => f.properties || {}).filter((p) => PLACE_OSM_VALUES.has((p.osm_value || "").toLowerCase())).slice(0, 6);
        setResults(places);
        if (focusedRef.current) { setOpen(true); setActiveIdx(-1); }
      } catch (e) { if (e.name !== "AbortError") setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (p) => { const f = formatPhotonSuggestion(p); skipNextFetchRef.current = true; setQuery(f); onChange(f); setOpen(false); setResults([]); };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !(open && activeIdx >= 0)) { if (onEnter) onEnter(); return; }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? results.length - 1 : i - 1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(results[activeIdx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</label>}
      <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 focus-within:bg-white transition-all"
        style={{ }} onFocusCapture={(e) => { e.currentTarget.style.borderColor = focusColor; }} onBlurCapture={(e) => { e.currentTarget.style.borderColor = ""; }}>
        <MapPin className="w-4 h-4 text-slate-400 mr-3 shrink-0" />
        <input data-testid={inputTestId} type="text" value={query}
          onChange={(e) => { focusedRef.current = true; setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => { focusedRef.current = true; if (results.length) setOpen(true); }}
          onBlur={() => { focusedRef.current = false; }} onKeyDown={onKeyDown}
          placeholder={placeholder} autoComplete="off"
          className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm" />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin ml-2 shrink-0" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid={suggestionsTestId} className="absolute z-30 mt-2 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] overflow-hidden">
          {results.map((p, i) => {
            const formatted = formatPhotonSuggestion(p);
            const active = i === activeIdx;
            return (
              <button key={`${p.osm_id || p.name}-${i}`} type="button" data-testid={`${inputTestId}-suggestion-${i}`}
                onMouseEnter={() => setActiveIdx(i)} onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${active ? "bg-[#1D6AFF]/8" : "hover:bg-slate-50"}`}>
                <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-[#1D6AFF]" : "text-slate-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{formatted}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-1">{p.osm_value}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
